import React, {useEffect, useRef, useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {Platform, StyleSheet, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {Icon} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import RewardsBanner from '../../components/rewards/RewardsBanner';
import Button from '../../components/themed/Button';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import Dialog from '../../components/ui-controls/Dialog';
import ContentView from '../../components/ui-controls/layout/ContentView';
import PressableWithOpacityFeedback from '../../components/ui-controls/PressableWithOpacityFeedback';
import FormField from '../../design-system/form/FormField';
import * as designTokens from '../../design-system/tokens';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {cleanPhoneNumber} from '../../utils/phoneNumber';
import {PhoneNumberOnlyFormData, phoneNumberOnlySchema} from '../account/schemas/authSchemas';
import SignUpPageLayout, {
  CTA_HEIGHT,
  CTA_RADIUS,
  useWideLayout,
} from '../account/signUp/SignUpPageLayout';

import {resetToHome} from './resetToHome';
import {AccountStatus} from './types';

// DIS-1384 R3/R19 shared canonical copy — byte-identical to
// LmgStaffSelectionScreen; the dash is a real em-dash (—), not a hyphen.
const INVALID_INVITE_COPY = 'Your invite link is invalid — request a new one.';

// DIS-1730: copy carried over verbatim from the deleted "Send Code" step,
// which this screen now replaces.
const SEND_CODE_ERROR = 'Could not send verification code. Please try again.';

const RewardsSignInScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.REWARDS_SIGN_IN>) => {
    const {offersStore, rewardsStore, shopSelectionStore} = useStores();
    const {isDesktopCard: isWide, onLayout} = useWideLayout();

    // DIS-1228: params now typed via RootRewardsFlowParamList — query string
    // is parsed/normalised in linking.ts (decodeURIComponent → trim on
    // signUpCode). Case is deliberately PRESERVED (linking.ts:357), so the
    // DIS-1660 validation below is a case-sensitive match against the offer
    // code exactly as authored.
    const params = route.params;
    const isStaff = params?.staff === 'true';

    // DIS-1384 R1: the emailed LMG deep-link carries an opaque invite secret as
    // `lmgCode`. The linking parser injects it verbatim (decoded, NOT trimmed),
    // but the REWARDS_SIGN_IN param type doesn't yet declare it, so read via a
    // local cast rather than widening the shared navigationTypes here.
    const lmgCode = (params as {lmgCode?: string} | undefined)?.lmgCode;
    const hasLmgCode = typeof lmgCode === 'string' && lmgCode !== '';

    // R3: an explicit invalid invite (200 valid:false OR 403) blocks advancing;
    // any transient error fails open (submit-time 403 is the real gate, C2).
    const [inviteInvalid, setInviteInvalid] = useState(false);

    // DIS-1660 Decision 2: the verdict is DERIVED on every render, never frozen in
    // a mount-time effect. Reading these observables inside the observer render is
    // also what establishes the MobX subscription that lazily starts the offers
    // fetch (offersStore.ts:187 onBecomeObserved) and re-renders when it resolves.
    const urlSignUpCode = params?.signUpCode;
    const signUpOffers = offersStore.signUpOfferData;
    const isLoadingOffers = offersStore.isLoadingSignUpOffers;

    // Three states, spec Decision 4. Loading/absent/empty is UNVERIFIABLE, never
    // invalid: offersStore.ts:206 collapses a fetch failure to [], so an empty
    // list is not proof the code is unknown. Unverifiable fails OPEN.
    const signUpCodeVerdict: 'none' | 'unverifiable' | 'valid' | 'invalid' = !urlSignUpCode
      ? 'none'
      : isLoadingOffers || !signUpOffers || signUpOffers.length === 0
        ? 'unverifiable'
        : signUpOffers.some(offer => offer.signUpCode === urlSignUpCode)
          ? 'valid'
          : 'invalid';

    // The dismissal is keyed on the code that was rejected, never a bare boolean.
    // RootRewardsFlowStackNavigator.tsx:58 sets detachInactiveScreens={true},
    // which detaches the native view but does NOT unmount this screen, so a
    // boolean survives a params update and would swallow the dialog for a
    // SECOND, different invalid code arriving on the same screen instance.
    const [dismissedSignUpCode, setDismissedSignUpCode] = useState<string | null>(null);
    const invalidCodeDismissed = dismissedSignUpCode === urlSignUpCode;

    // DIS-1660 Decision 5: removal must clear BOTH the store and the route param.
    // A surviving route param would re-fire the dialog; clearing the store alone
    // fails the other way — rewardsStore.signInRouteParams re-echoes signUpCode
    // into the URL. Only the rejected code is cleared from the store: an
    // unrelated code rehydrated from the in-flight snapshot (rewardsStore.ts:202)
    // must not be wiped by dismissing a bad URL code.
    const handleInvalidSignUpCodeOk = () => {
      setDismissedSignUpCode(urlSignUpCode ?? null);
      if (!rewardsStore.signUpCode || rewardsStore.signUpCode === urlSignUpCode) {
        rewardsStore.setSignUpCode('');
      }
      navigation.setParams({signUpCode: undefined});
    };

    useEffect(() => {
      if (!hasLmgCode) {
        return;
      }
      // R1: enter LMG mode and store the raw code verbatim (no trim/parse).
      rewardsStore.setIsLmgStaffFlow(true);
      rewardsStore.setLmgStaffCode(lmgCode);
      // R26 (LANDMINE): the emailed link carries BOTH staff=true AND lmgCode.
      // lmgCode wins — force the store-staff flag false so the store picker and
      // STAFF pill never leak onto the LMG path.
      rewardsStore.setIsStaffSignup(false);

      let cancelled = false;
      (async () => {
        try {
          const res = await apiService.rewardsLmgStaffInvite({code: lmgCode});
          if (cancelled) {
            return;
          }
          // Advisory (C2): block only on an explicit valid:false; a missing/true
          // flag proceeds.
          if (res?.data?.valid === false) {
            setInviteInvalid(true);
          }
        } catch (e) {
          // FAIL-OPEN on any transient error (5xx / timeout / network). Only an
          // explicit 403 blocks.
          const err = e as {
            response?: {status?: number};
            cause?: {response?: {status?: number}};
            status?: number;
          };
          const status = err?.response?.status ?? err?.cause?.response?.status ?? err?.status;
          if (status === 403 && !cancelled) {
            setInviteInvalid(true);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lmgCode]);

    const {
      control,
      handleSubmit,
      formState: {errors, touchedFields, isSubmitted, isSubmitting},
      setError,
    } = useForm<PhoneNumberOnlyFormData>({
      resolver: zodResolver(phoneNumberOnlySchema),
      defaultValues: {phoneNumber: ''},
      mode: 'onTouched',
    });

    // DIS-1730 (spec A5): NEXT is enabled only for a valid number. The gate is
    // derived from the watched value, never from formState.isValid: a
    // setError from a failed check or send marks the form invalid, and a gate
    // on isValid would then freeze NEXT until the user edited the field.
    const watchedPhoneNumber = useWatch({control, name: 'phoneNumber'});
    const isPhoneNumberValid = phoneNumberOnlySchema.safeParse({
      phoneNumber: watchedPhoneNumber,
    }).success;

    // DIS-1730 (spec S5): the send can resolve after the user has left the
    // screen. The deleted "Send Code" step guarded its navigate the same way.
    const mountedRef = useRef(true);
    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    // isSubmitting only disables NEXT after a re-render, so two presses in the
    // same frame would both reach onSubmit. This ref closes that window.
    const submitInFlightRef = useRef(false);

    // DIS-1730 (spec S1, S2): the "Send Code" step is gone. An existing member
    // gets the code sent from here and lands straight on verification, with
    // the flowType the old step was given.
    const sendCodeAndVerify = async (flowType: AccountStatus) => {
      try {
        const result = await apiService.rewardsVerifyExistingUserPhoneNumber({
          phoneNumber: rewardsStore.phoneNumber,
        });
        if (!mountedRef.current) {
          return;
        }
        if (result?.status === 200) {
          navigation.navigate(AppRoute.REWARDS_VERIFICATION, {flowType});
          return;
        }
      } catch (_e) {
        if (!mountedRef.current) {
          return;
        }
      }
      setError('phoneNumber', {type: 'manual', message: SEND_CODE_ERROR});
    };

    // The account check and routing. onSubmit wraps it in the in-flight guard.
    const checkAccount = async (values: PhoneNumberOnlyFormData) => {
      const phoneNumber = cleanPhoneNumber(values.phoneNumber || '');

      try {
        const result = await apiService.rewardsCheckAccountExists({
          phoneNumber,
        });
        if (result?.status === 200) {
          rewardsStore.setPhoneNumber(phoneNumber);

          // Set signUpCode if provided in query parameters. DIS-1660: gated on the
          // CURRENT verdict, so a list that resolves between mount and submit is
          // honoured and a rejected code is never re-written.
          if (params?.signUpCode && signUpCodeVerdict !== 'invalid') {
            rewardsStore.setSignUpCode(params.signUpCode);
          }

          // DIS-1135 bug_002: set unconditionally so a non-staff sign-in on a
          // device that previously hosted a staff sign-in clears the persisted
          // flag. Otherwise routing falls into verifyStaffPhone (401) and
          // MemberCard renders STAFF badge for the wrong user.
          // DIS-1384 R26: on the LMG path lmgCode wins — never let staff=true set
          // the store-staff flag.
          rewardsStore.setIsStaffSignup(hasLmgCode ? false : isStaff);

          // DIS-1646: the DIS-1135 bug_002 clear above never got an LMG
          // counterpart when DIS-1384 added these flags. The LMG effect returns
          // early without an lmgCode, so a consumer sign-in on a device that
          // previously ran an LMG invite kept isLmgStaff true and a later
          // completion dump() persisted it onto the wrong account. Badge
          // staleness is prevented here, on flow entry, so a local true is only
          // ever set by the flow currently running.
          if (!hasLmgCode) {
            rewardsStore.setIsLmgStaffFlow(false);
            rewardsStore.setIsLmgStaff(false);
            rewardsStore.setLmgStaffCode('');
          }

          const accountStatus = result.data?.accountStatus;
          switch (accountStatus) {
            case AccountStatus.SIGNUP_LITE:
              // Brand new user — no Zoho or Zen account
              navigation.navigate(AppRoute.REWARDS_SIGN_UP, {
                flowType: AccountStatus.SIGNUP_LITE,
              });
              break;
            case AccountStatus.REWARDS_SIGNUP:
              // Exists in Zoho but not Zen — opt-in flow
              await sendCodeAndVerify(AccountStatus.REWARDS_SIGNUP);
              break;
            case AccountStatus.ACTIVATE_REWARDS:
              // Exists in both, card inactive — opt-in flow
              await sendCodeAndVerify(AccountStatus.ACTIVATE_REWARDS);
              break;
            case AccountStatus.NO_LITE_SIGNUP:
              if (isStaff) {
                // Staff signup for existing member — run through activate flow
                // to assign staff group on their existing card
                await sendCodeAndVerify(AccountStatus.ACTIVATE_REWARDS);
              } else {
                // User has active rewards — verify phone → display card via Success screen.
                // Applies to both logged-in and non-logged-in users (e.g. Zen-only card holders).
                // Backend runs as lite-sign-in: verifies phone, returns existing Zen card data.
                await sendCodeAndVerify(AccountStatus.NO_LITE_SIGNUP);
              }
              break;
            default:
              await sendCodeAndVerify(AccountStatus.NO_SIGNUP);
              break;
          }
        } else {
          setError('phoneNumber', {type: 'manual', message: 'Something went wrong'});
        }
      } catch (_e) {
        setError('phoneNumber', {
          type: 'manual',
          message: 'Could not check account. Please try again.',
        });
      }
    };

    const onSubmit = async (values: PhoneNumberOnlyFormData) => {
      // R3: an explicit invalid invite blocks advancing past the landing.
      if (inviteInvalid || submitInFlightRef.current) {
        return;
      }
      submitInFlightRef.current = true;
      try {
        await checkAccount(values);
      } finally {
        submitInFlightRef.current = false;
      }
    };

    // DIS-1827: X always leaves the flow for Home. A back press is wrong here:
    // in-flow Back buttons navigate to this screen again, so goBack() could
    // return to a sign-up step and loop between X and Back.
    const handleClose = () => {
      resetToHome(!!shopSelectionStore?.selectedStore?.id);
    };

    const headingCopy = isStaff ? 'WELCOME STAFF MEMBERS!' : 'WELCOME';
    const introCopy = `Unlock free ${isStaff ? 'staff' : 'member'} benefits in just a few steps. Enter your mobile number to get started.`;

    const mobileField = (
      <Controller
        control={control}
        name="phoneNumber"
        render={({field: {onChange, onBlur, value}}) => (
          <FormField
            label="Mobile"
            required
            error={!!errors.phoneNumber && (!!touchedFields.phoneNumber || isSubmitted)}
            errorMessage={errors.phoneNumber?.message}
            onChangeText={(text: string) => {
              // ios safari autofill doesn't include country code
              // when autofill number starting with +61 so add it here
              if (text.length === 9 && text?.[0] === '4') {
                text = '+61' + text;
              }
              onChange(text);
            }}
            value={value || ''}
            autoCapitalize="none"
            autoComplete="tel"
            keyboardType="phone-pad"
            maxLength={20}
            onBlur={onBlur}
            testID="rewards-sign-in-mobile"
          />
        )}
      />
    );

    const inviteNotice = inviteInvalid ? (
      <Text style={isWide ? styles.inviteNoticeWide : {marginTop: 16}}>{INVALID_INVITE_COPY}</Text>
    ) : null;

    const nextButton = (
      <Button
        title={'NEXT'}
        onPress={handleSubmit(onSubmit)}
        disabled={isSubmitting || inviteInvalid || !isPhoneNumberValid}
        loading={isSubmitting}
        buttonStyle={styles.cta}
        disabledStyle={styles.ctaDisabled}
        titleStyle={styles.ctaTitle}
        disabledTitleStyle={styles.ctaTitleDisabled}
        testID="rewards-sign-in-next"
      />
    );

    // Desktop (536:4247): the centred card. No close X on wide (D7).
    const wideContent = (
      <SignUpPageLayout
        centred
        isWide={isWide}
        onLayout={onLayout}
        testID="rewardsSignInScreen"
        banner={<RewardsBanner />}
        heading={
          <Text style={[designTokens.type.heading.h1, styles.headingWide]}>{headingCopy}</Text>
        }
        // D2: desktop body is P1; mobile keeps P2 as drawn.
        intro={<Text style={[designTokens.type.paragraph.p1, styles.introWide]}>{introCopy}</Text>}
        main={
          <>
            {mobileField}
            {inviteNotice}
          </>
        }
        aside={nextButton}
      />
    );

    // Mobile: unchanged. onLayout measures the width that picks the layout.
    const narrowContent = (
      <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}} onLayout={onLayout}>
        {/* D7: native keeps the X because a cold deep link to /rewards/sign-in
            has no other exit (the root stack has no initialRouteName); web has
            browser back. */}
        {Platform.OS !== 'web' ? (
          <View style={styles.closeRow}>
            <PressableWithOpacityFeedback
              onPress={handleClose}
              disabled={isSubmitting}
              accessibilityRole="button"
              accessibilityLabel="Close"
              testID="rewards-sign-in-close">
              {/* @rneui hardcodes accessibilityRole="button" on its icon
                  container; the Pressable carries the role (see
                  SignUpEmailScreenContent). */}
              <Icon name="close" type="material" size={CLOSE_ICON_SIZE} accessibilityRole="none" />
            </PressableWithOpacityFeedback>
          </View>
        ) : null}

        <RewardsBanner style={styles.banner} />

        <Text style={[designTokens.type.heading.h1, styles.heading]}>{headingCopy}</Text>
        <Text style={[designTokens.type.paragraph.p2, styles.intro]}>{introCopy}</Text>

        <View style={styles.field}>{mobileField}</View>
        {inviteNotice}
        <View style={styles.ctaRow}>{nextButton}</View>
      </ContentView>
    );

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        {isWide ? wideContent : narrowContent}

        {/* DIS-1660: Alert.alert is a no-op on react-native-web, and a
            registration deep link is a web URL — the popup must be an in-app
            modal. Dialog with cancelVisible={false} and no onCancel gives a
            single OK button and an inert backdrop. */}
        {signUpCodeVerdict === 'invalid' && !invalidCodeDismissed ? (
          <Dialog
            isVisible
            cancelVisible={false}
            okTestID="signup-code-invalid-ok"
            onOk={handleInvalidSignUpCodeOk}
            title="The sign up code is not valid. Click OK to continue to remove."
          />
        ) : null}
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsSignInScreen;

// SHORTCUT: spacing literals - CLOSE_ICON_SIZE, the narrow invite notice's
// marginTop 16 in the render, and every narrow margin literal below. They
// follow SignUpEmailScreenContent (close icon 24, 16 above the banner) and the
// 5.png board. Ceiling: this screen. Upgrade path: swap the remaining
// literals for space tokens in a later DIS-1735 pass. The
// colours are theme-resolved, not literals.
const CLOSE_ICON_SIZE = 24;

const styles = StyleSheet.create({
  closeRow: {
    alignItems: 'flex-end',
  },
  banner: {
    marginTop: 16,
  },
  heading: {
    color: theme.colour.text.heading.primary,
    marginTop: 24,
    textAlign: 'center',
  },
  intro: {
    marginTop: 16,
    textAlign: 'center',
  },
  // Wide only: the centred card spaces the heading and intro itself. Line
  // heights from 536:4505 (H1 20 tall, P1 two lines in 38).
  headingWide: {
    ...designTokens.lineHeight.heading.h1,
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
  },
  introWide: {
    ...designTokens.lineHeight.paragraph.p1,
    textAlign: 'center',
  },
  // Wide only: a field-attached message, centred in the form column.
  inviteNoticeWide: {
    marginTop: designTokens.layout.desktop.stackRelated,
    textAlign: 'center',
  },
  field: {
    marginTop: designTokens.space.s40,
  },
  ctaRow: {
    marginTop: 24,
  },
  // Same CTA as the create-account email step (SignUpEmailScreenContent).
  cta: {
    width: '100%',
    height: CTA_HEIGHT,
    borderRadius: CTA_RADIUS,
    backgroundColor: theme.colour.background.button.primary,
  },
  ctaDisabled: {
    backgroundColor: theme.colour.background.button.disabled,
  },
  ctaTitle: {
    ...designTokens.type.button.primary,
    color: theme.colour.background.button.contrastOnPrimary,
  },
  ctaTitleDisabled: {
    color: theme.colour.text.button.disabled,
  },
});
